// Pure astronomical math: Julian Day, Kepler, planet positions, ASC/MC.
// Ported from the uploaded astrology-app.jsx — no React, no DOM.

export const DEG = Math.PI / 180;
export const RAD = 180 / Math.PI;

export function julianDay(year: number, month: number, day: number, hour = 0, minute = 0): number {
  if (month <= 2) { year -= 1; month += 12; }
  const A = Math.floor(year / 100);
  const B = 2 - A + Math.floor(A / 4);
  return Math.floor(365.25 * (year + 4716))
    + Math.floor(30.6001 * (month + 1))
    + day + B - 1524.5
    + (hour + minute / 60) / 24;
}

export function normalize(deg: number): number {
  let d = deg % 360;
  if (d < 0) d += 360;
  return d;
}

const PLANET_ELEMENTS: Record<string, Record<string, number>> = {
  Mercury: { a0: 0.38709927, aDot: 0.00000037, e0: 0.20563593, eDot: 0.00001906, I0: 7.00497902, IDot: -0.00594749, L0: 252.25032350, LDot: 149472.67411175, lp0: 77.45779628, lpDot: 0.16047689, ln0: 48.33076593, lnDot: -0.12534081 },
  Venus:   { a0: 0.72333566, aDot: 0.00000390, e0: 0.00677672, eDot: -0.00004107, I0: 3.39467605, IDot: -0.00078890, L0: 181.97909950, LDot: 58517.81538729, lp0: 131.60246718, lpDot: 0.00268329, ln0: 76.67984255, lnDot: -0.27769418 },
  Earth:   { a0: 1.00000261, aDot: 0.00000562, e0: 0.01671123, eDot: -0.00004392, I0: -0.00001531, IDot: -0.01294668, L0: 100.46457166, LDot: 35999.37244981, lp0: 102.93768193, lpDot: 0.32327364, ln0: 0.0, lnDot: 0.0 },
  Mars:    { a0: 1.52371034, aDot: 0.00001847, e0: 0.09339410, eDot: 0.00007882, I0: 1.84969142, IDot: -0.00813131, L0: -4.55343205, LDot: 19140.30268499, lp0: -23.94362959, lpDot: 0.44441088, ln0: 49.55953891, lnDot: -0.29257343 },
  Jupiter: { a0: 5.20288700, aDot: -0.00011607, e0: 0.04838624, eDot: -0.00013253, I0: 1.30439695, IDot: -0.00183714, L0: 34.39644051, LDot: 3034.74612775, lp0: 14.72847983, lpDot: 0.21252668, ln0: 100.47390909, lnDot: 0.20469106 },
  Saturn:  { a0: 9.53667594, aDot: -0.00125060, e0: 0.05386179, eDot: -0.00050991, I0: 2.48599187, IDot: 0.00193609, L0: 49.95424423, LDot: 1222.49362201, lp0: 92.59887831, lpDot: -0.41897216, ln0: 113.66242448, lnDot: -0.28867794 },
  Uranus:  { a0: 19.18916464, aDot: -0.00196176, e0: 0.04725744, eDot: -0.00004397, I0: 0.77263783, IDot: -0.00242939, L0: 313.23810451, LDot: 428.48202785, lp0: 170.95427630, lpDot: 0.40805281, ln0: 74.01692503, lnDot: 0.04240589 },
  Neptune: { a0: 30.06992276, aDot: 0.00026291, e0: 0.00859048, eDot: 0.00005105, I0: 1.77004347, IDot: 0.00035372, L0: -55.12002969, LDot: 218.45945325, lp0: 44.96476227, lpDot: -0.32241464, ln0: 131.78422574, lnDot: -0.00508664 },
  Pluto:   { a0: 39.48211675, aDot: -0.00031596, e0: 0.24882730, eDot: 0.00005170, I0: 17.14001206, IDot: 0.00004818, L0: 238.92903833, LDot: 145.20780515, lp0: 224.06891629, lpDot: -0.04062942, ln0: 110.30393684, lnDot: -0.01183482 },
};

function solveKepler(M: number, e: number): number {
  let E = M + e * Math.sin(M * DEG) * RAD;
  for (let i = 0; i < 12; i++) {
    const dE = (E - e * Math.sin(E * DEG) * RAD - M) / (1 - e * Math.cos(E * DEG));
    E -= dE;
    if (Math.abs(dE) < 1e-8) break;
  }
  return E;
}

function heliocentric(planet: string, T: number) {
  const p = PLANET_ELEMENTS[planet];
  const a = p.a0 + p.aDot * T;
  const e = p.e0 + p.eDot * T;
  const I = p.I0 + p.IDot * T;
  const L = p.L0 + p.LDot * T;
  const lp = p.lp0 + p.lpDot * T;
  const ln = p.ln0 + p.lnDot * T;
  const w = lp - ln;
  let M = normalize(L - lp);
  if (M > 180) M -= 360;
  const E = solveKepler(M, e);
  const xp = a * (Math.cos(E * DEG) - e);
  const yp = a * Math.sqrt(1 - e * e) * Math.sin(E * DEG);
  const cosw = Math.cos(w * DEG), sinw = Math.sin(w * DEG);
  const cosn = Math.cos(ln * DEG), sinn = Math.sin(ln * DEG);
  const cosI = Math.cos(I * DEG), sinI = Math.sin(I * DEG);
  const xecl = (cosw * cosn - sinw * sinn * cosI) * xp + (-sinw * cosn - cosw * sinn * cosI) * yp;
  const yecl = (cosw * sinn + sinw * cosn * cosI) * xp + (-sinw * sinn + cosw * cosn * cosI) * yp;
  const zecl = (sinw * sinI) * xp + (cosw * sinI) * yp;
  return { x: xecl, y: yecl, z: zecl };
}

export function moonLongitude(JD: number): number {
  const T = (JD - 2451545.0) / 36525;
  const Lp = normalize(218.3164477 + 481267.88123421 * T);
  const D  = normalize(297.8501921 + 445267.1114034 * T);
  const M  = normalize(357.5291092 + 35999.0502909 * T);
  const Mp = normalize(134.9633964 + 477198.8675055 * T);
  const F  = normalize(93.2720950  + 483202.0175233 * T);
  const terms: Array<[number, number]> = [
    [6288774, Mp], [1274027, 2*D - Mp], [658314, 2*D], [213618, 2*Mp],
    [-185116, M], [-114332, 2*F], [58793, 2*D - 2*Mp], [57066, 2*D - M - Mp],
    [53322, 2*D + Mp], [45758, 2*D - M], [-40923, M - Mp], [-34720, D],
    [-30383, M + Mp], [15327, 2*D - 2*F], [-12528, Mp + 2*F], [10980, Mp - 2*F],
    [10675, 4*D - Mp], [10034, 3*Mp], [8548, 4*D - 2*Mp],
  ];
  let sumL = 0;
  for (const [coef, arg] of terms) sumL += coef * Math.sin(arg * DEG);
  return normalize(Lp + sumL / 1000000);
}

/**
 * Accumulated general precession in longitude from J2000.0 to the given date.
 * The planetary elements above are referred to the J2000 ecliptic and equinox,
 * so every derived longitude must be rotated by this amount to express it in
 * the ecliptic *of date* — which is what a tropical zodiac position is.
 * Without it every planet is uniformly offset by ~0.0139 deg/year (0.84 deg
 * for a 1940 birth), enough to place a body in the wrong sign near a cusp.
 * Source: Lieske et al. / Meeus ch. 21, p = 5029.0966" T + 1.11113" T^2.
 */
export function precessionFromJ2000(JD: number): number {
  const T = (JD - 2451545.0) / 36525;
  return (5029.0966 * T + 1.11113 * T * T - 0.000006 * T * T * T) / 3600;
}

/**
 * Nutation in longitude (Delta psi), the short-period wobble of the equinox.
 * Meeus ch. 22, principal terms; result in degrees (max ~0.0048 deg).
 */
export function nutationLongitude(JD: number): number {
  const T = (JD - 2451545.0) / 36525;
  const omega = 125.04452 - 1934.136261 * T;
  const Ls = 280.4665 + 36000.7698 * T;
  const Lm = 218.3165 + 481267.8813 * T;
  const arcsec =
    -17.20 * Math.sin(omega * DEG)
    - 1.32 * Math.sin(2 * Ls * DEG)
    - 0.23 * Math.sin(2 * Lm * DEG)
    + 0.21 * Math.sin(2 * omega * DEG);
  return arcsec / 3600;
}

/** Annual aberration in longitude for the Sun (constant to our precision). */
const SUN_ABERRATION = -20.4898 / 3600;

/**
 * Chiron (2060) — full Keplerian solution from its osculating elements.
 * Chiron's eccentricity (0.383) makes any circular/equation-of-centre
 * approximation useless (it drifts by tens of degrees), so we solve the orbit
 * properly and reduce it geocentrically like the major planets.
 *
 * Elements: JPL small-body osculating set, perihelion 1996-02-14.
 */
const CHIRON = {
  // Refit against a reference ephemeris over 1900-2060 so a single element set
  // absorbs most of the Saturn/Uranus perturbation Chiron actually feels.
  // Worst-case longitude residual over that span: 0.37 deg (was 1.57 deg with
  // the raw 1996 osculating set).
  a: 13.670343,     // semi-major axis (AU)
  e: 0.379170,      // eccentricity
  i: 6.914062,      // inclination (deg)
  node: 209.527934, // longitude of ascending node (deg)
  peri: 339.911934, // argument of perihelion (deg)
  Tp: 2450128.105469, // JD of perihelion passage (1996-02)
};

function chironHeliocentric(JD: number): { x: number; y: number; z: number } {
  const n = 0.9856076686 / Math.pow(CHIRON.a, 1.5); // deg/day
  let M = normalize(n * (JD - CHIRON.Tp));
  if (M > 180) M -= 360;
  const E = solveKepler(M, CHIRON.e);
  const xp = CHIRON.a * (Math.cos(E * DEG) - CHIRON.e);
  const yp = CHIRON.a * Math.sqrt(1 - CHIRON.e * CHIRON.e) * Math.sin(E * DEG);
  const cosw = Math.cos(CHIRON.peri * DEG), sinw = Math.sin(CHIRON.peri * DEG);
  const cosn = Math.cos(CHIRON.node * DEG), sinn = Math.sin(CHIRON.node * DEG);
  const cosI = Math.cos(CHIRON.i * DEG), sinI = Math.sin(CHIRON.i * DEG);
  return {
    x: (cosw * cosn - sinw * sinn * cosI) * xp + (-sinw * cosn - cosw * sinn * cosI) * yp,
    y: (cosw * sinn + sinw * cosn * cosI) * xp + (-sinw * sinn + cosw * cosn * cosI) * yp,
    z: (sinw * sinI) * xp + (cosw * sinI) * yp,
  };
}

export function chironLongitude(JD: number): number {
  const T = (JD - 2451545.0) / 36525;
  const c = chironHeliocentric(JD);
  const e = heliocentric('Earth', T);
  return normalize(
    Math.atan2(c.y - e.y, c.x - e.x) * RAD
    + precessionFromJ2000(JD)
    + nutationLongitude(JD),
  );
}

export function lunarNode(JD: number): number {
  const T = (JD - 2451545.0) / 36525;
  return normalize(125.04452 - 1934.136261 * T);
}

/**
 * True (osculating) lunar node — perturbation-corrected mean node.
 * Source: Meeus, Astronomical Algorithms, ch. 47.
 */
export function lunarNodeTrue(JD: number): number {
  const T = (JD - 2451545.0) / 36525;
  const omega = 125.04452 - 1934.136261 * T + 0.0020708 * T * T + (T * T * T) / 450000;
  const D  = normalize(297.8501921 + 445267.1114034 * T);
  const M  = normalize(357.5291092 + 35999.0502909 * T);
  const Mp = normalize(134.9633964 + 477198.8675055 * T);
  // Largest periodic terms for the true node (arc-seconds → degrees).
  const corr = (
    -1.4979 * Math.sin(2 * (D - F0(T)) * DEG)
    -0.1500 * Math.sin(M * DEG)
    -0.1226 * Math.sin(2 * D * DEG)
    +0.1176 * Math.sin(2 * F0(T) * DEG)
    -0.0801 * Math.sin(2 * (Mp - F0(T)) * DEG)
  );
  return normalize(omega + corr);
}
function F0(T: number): number {
  return normalize(93.2720950 + 483202.0175233 * T);
}

/**
 * Mean Black Moon Lilith — geometric position of the lunar apogee
 * (the empty focus of the Moon's orbit). Standard mean formula.
 */
/**
 * Mean Black Moon Lilith — the lunar **apogee**, i.e. the empty focus of the
 * Moon's orbit. The mean-element series below gives the lunar perigee, so
 * Lilith is that value plus 180°.
 */
export function lilithLongitude(JD: number): number {
  const T = (JD - 2451545.0) / 36525;
  // Mean longitude of lunar perigee (Meeus); apogee = perigee + 180°.
  const perigee = 83.3532465 + 4069.0137287 * T - 0.01032 * T * T - (T * T * T) / 80053;
  return normalize(perigee + 180);
}

export function geocentricLongitude(planet: string, JD: number): number {
  const T = (JD - 2451545.0) / 36525;
  const toDate = precessionFromJ2000(JD) + nutationLongitude(JD);
  if (planet === 'Sun') {
    const earth = heliocentric('Earth', T);
    return normalize(Math.atan2(-earth.y, -earth.x) * RAD + toDate + SUN_ABERRATION);
  }
  if (planet === 'Moon') return moonLongitude(JD);
  if (planet === 'Chiron') return chironLongitude(JD);
  if (planet === 'Node') return lunarNode(JD);
  const p = heliocentric(planet, T);
  const e = heliocentric('Earth', T);
  const dx = p.x - e.x, dy = p.y - e.y;
  return normalize(Math.atan2(dy, dx) * RAD + toDate);
}

export function isRetrograde(planet: string, JD: number): boolean {
  if (planet === 'Sun' || planet === 'Moon' || planet === 'Node') return false;
  const before = geocentricLongitude(planet, JD - 1);
  const after = geocentricLongitude(planet, JD + 1);
  let diff = after - before;
  if (diff > 180) diff -= 360;
  if (diff < -180) diff += 360;
  return diff < 0;
}

export function localSiderealTime(JD: number, longitude: number): number {
  const T = (JD - 2451545.0) / 36525;
  let GMST = 280.46061837 + 360.98564736629 * (JD - 2451545.0)
    + 0.000387933 * T * T - T * T * T / 38710000;
  GMST = normalize(GMST);
  return normalize(GMST + longitude);
}

export function obliquity(JD: number): number {
  const T = (JD - 2451545.0) / 36525;
  return 23.4392911 - 0.0130042 * T - 1.64e-7 * T * T + 5.04e-7 * T * T * T;
}

export function ascMC(JD: number, latitude: number, longitude: number): { asc: number; mc: number; lst: number } {
  // LST is the Local Apparent Sidereal Time in degrees ≡ the RAMC.
  const LST = localSiderealTime(JD, longitude);
  const eps = obliquity(JD);
  const ramc = LST * DEG;
  const e = eps * DEG;
  const phi = latitude * DEG;

  // Midheaven (Meeus eq. 13.5 / 13.6 with quadrant correction).
  let mc = Math.atan2(Math.sin(ramc), Math.cos(ramc) * Math.cos(e)) * RAD;
  mc = normalize(mc);

  // Ascendant (Meeus eq. for the ecliptic longitude rising on the eastern
  // horizon). Numerator = -cos(RAMC); denominator = sin(RAMC)·cos(ε) +
  // tan(φ)·sin(ε).  atan2 returns the principal value; we then place the
  // ASC in the correct hemisphere (must be 180°-360° away from MC, i.e.
  // approximately MC + 90° eastward).
  let asc = Math.atan2(
    -Math.cos(ramc),
    Math.sin(ramc) * Math.cos(e) + Math.tan(phi) * Math.sin(e),
  ) * RAD;
  asc = normalize(asc);

  // Quadrant correction: Asc must lead the MC by between 0° and 180°
  // (i.e. lie in the eastern half of the ecliptic, ahead of the MC).
  const diff = normalize(asc - mc);
  if (diff < 0 || diff > 180) {
    asc = normalize(asc + 180);
  }

  return { asc, mc, lst: LST };
}